// UIContext.tsx
import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from "react";
import { addLocale, locale } from "primereact/api";

export type Toast = {
    id: string;
    text: string;
    content: ReactNode;
};

interface UIContextProps {
    toasts: Toast[];
    showToast: (text: string, content?: ReactNode, timeVisible?: number) => void;
    removeToast: (toastId: string) => void;
    currentZoom: number;
    setCurrentZoom: React.Dispatch<React.SetStateAction<number>>;
    boardScrollPosition: [number, number] | undefined;
    setBoardScrollPosition: React.Dispatch<React.SetStateAction<[number, number] | undefined>>;
    currentLocale: string;
    setLocale: (localeCode: string) => void;
    showLoader: boolean;
    handleShowLoader: (show: boolean) => void;
}

const UIContext = createContext<UIContextProps | undefined>(undefined);

export const useUI = () => {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error("useUI must be used within a UIProvider");
    }
    return context;
};

interface UIProviderProps {
    children: ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const [currentZoom, setCurrentZoom] = useState<number>(1);
    const [boardScrollPosition, setBoardScrollPosition] = useState<[number, number] | undefined>();
    const [currentLocale, setCurrentLocale] = useState("ru");
    const [showLoader, setShowLoader] = useState<boolean>(false);
    
    const handleShowLoader = (show: boolean) => {
        setShowLoader(show);
    };
    
    useEffect(() => {
        addLocale("ru", {
            firstDayOfWeek: 1,
            dayNames: ["Воскресенье", "Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота"],
            dayNamesShort: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
            dayNamesMin: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
            monthNames: [
                "Январь",
                "Февраль",
                "Март",
                "Апрель",
                "Май",
                "Июнь",
                "Июль",
                "Август",
                "Сентябрь",
                "Октябрь",
                "Ноябрь",
                "Декабрь",
            ],
            monthNamesShort: ["Янв", "Фев", "Март", "Апр", "Май", "Июнь", "Июль", "Авг", "Сен", "Окт", "Нояб", "Дек"],
            today: "Сегодня",
            clear: "Очистить",
        });

        locale(currentLocale);
    }, [currentLocale]);

    const showToast = useCallback(
        (text: string, content?: ReactNode, timeVisible?: number, details?: string,) => {
            if (details) {
                console.error('Непредвиденная ошибка', details)
                return
            }
            const TIME_VISIBLE = timeVisible ?? 4000;
            const id = crypto.randomUUID();
            setToasts((prevToasts) => [...prevToasts, { id, content, text }]);
            setTimeout(() => {
                setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
            }, TIME_VISIBLE);
        },
        [setToasts]
    );

    const removeToast = useCallback(
        (toastId: string): void => {
            setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== toastId));
        },
        [setToasts]
    );

    const handleSetLocale = useCallback((localeCode: string) => {
        setCurrentLocale(localeCode);
        locale(localeCode);
    }, []);

    const value: UIContextProps = {
        toasts,
        showToast,
        removeToast,
        currentZoom,
        setCurrentZoom,
        boardScrollPosition,
        setBoardScrollPosition,
        currentLocale,
        setLocale: handleSetLocale,
        showLoader,
        handleShowLoader
    };

    return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};
